import { useState, useEffect } from "react";
import "@/App.css";
import axios from "axios";
import { Toaster } from "@/components/ui/sonner";
import { CheckCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { LegalModal } from "@/components/LegalPages";
import Navigation from "@/components/Navigation";
import HeroSection from "@/components/HeroSection";
import ServicesSection from "@/components/ServicesSection";
import PowerGainsSection from "@/components/PowerGainsSection";
import ContactSection from "@/components/ContactSection";
import Footer, { WhatsAppButton } from "@/components/Footer";
import Shop from "@/components/Shop";
import PopularProducts from "@/components/PopularProducts";
import RecommendedForCar from "@/components/RecommendedForCar";
import AdminDashboard from "@/components/AdminDashboard";
import DealerSection from "@/components/DealerSection";
import InvoicePortal from "@/components/InvoicePortal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TOAST_OPTIONS = {
  style: {
    background: '#1A1A1A',
    border: '1px solid #2A2A2A',
    color: '#FAFAFA'
  }
};

function App() {
  const [legalPage, setLegalPage] = useState(null);
  const [showShop, setShowShop] = useState(false);
  const [initialProduct, setInitialProduct] = useState(null);
  const [initialCarMake, setInitialCarMake] = useState(null);
  const [paymentResult, setPaymentResult] = useState(null);
  const [isAdmin] = useState(() => window.location.pathname === "/admin");
  const [invoiceToken] = useState(() => {
    const m = window.location.pathname.match(/^\/factuur\/([^/]+)$/);
    return m ? m[1] : null;
  });

  useEffect(() => {
    if (showShop) window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [showShop]);

  // Handle payment return from Mollie
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('payment') === 'success') {
      const orderId = params.get('order');
      setShowShop(true);
      setPaymentResult({ orderId, status: 'checking' });
      if (orderId) {
        axios.get(`${API}/orders/${orderId}/status`)
          .then(r => setPaymentResult({ orderId, status: r.data.status, total: r.data.total }))
          .catch(() => setPaymentResult({ orderId, status: 'received' }));
      }
      window.history.replaceState({}, '', window.location.pathname);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (isAdmin) return <AdminDashboard />;
  if (invoiceToken) return <InvoicePortal token={invoiceToken} />;

  return (
    <div className="App bg-[#0A0A0A] min-h-screen">
      <Toaster position="top-right" toastOptions={TOAST_OPTIONS} />
      {legalPage && <LegalModal page={legalPage} onClose={() => setLegalPage(null)} />}
      <WhatsAppButton />
      <Navigation onShopClick={setShowShop} showShop={showShop} />

      {paymentResult && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/80 backdrop-blur-sm" data-testid="payment-result-modal">
          <div className="bg-[#111] border border-[#2A2A2A] rounded-2xl p-8 max-w-md mx-4 text-center">
            <CheckCircle size={56} className="text-[#6AB04C] mx-auto mb-4" />
            <h2 className="text-white text-2xl font-bold mb-2">
              {paymentResult.status === 'paid' ? 'Betaling Geslaagd!' : 'Bestelling Ontvangen!'}
            </h2>
            <p className="text-[#8A8A8A] text-sm mb-4">
              {paymentResult.status === 'paid'
                ? 'Je betaling is succesvol verwerkt. We gaan direct aan de slag met je bestelling!'
                : 'Je bestelling wordt verwerkt. We sturen je een bevestiging per email.'}
            </p>
            {paymentResult.orderId && (
              <p className="text-[#555] text-xs mb-6">
                Bestelnummer: <span className="text-[#FF3B30] font-mono">{paymentResult.orderId.slice(0, 8).toUpperCase()}</span>
              </p>
            )}
            <Button onClick={() => setPaymentResult(null)} className="bg-[#FF3B30] hover:bg-[#E0342A] text-white px-8 py-3 rounded-lg font-semibold" data-testid="payment-result-close">
              Verder winkelen
            </Button>
          </div>
        </div>
      )}

      {showShop ? (
        <Shop
          onBack={() => { setShowShop(false); setInitialProduct(null); setInitialCarMake(null); }}
          initialProduct={initialProduct}
          initialCarMake={initialCarMake}
          onContactClick={() => {
            setShowShop(false);
            setTimeout(() => document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" }), 100);
          }}
        />
      ) : (
        <main>
          <HeroSection onShopClick={() => setShowShop(true)} />
          <PopularProducts
            onShopClick={() => setShowShop(true)}
            onProductClick={(product) => { setInitialProduct(product); setShowShop(true); }}
          />
          <ServicesSection />
          <DealerSection />
          <RecommendedForCar
            onShopClick={(make) => { if (make) setInitialCarMake(make); setShowShop(true); }}
            onCarClick={(product) => { setInitialProduct(product); setShowShop(true); }}
          />
          <PowerGainsSection />
          <ContactSection />
        </main>
      )}

      <Footer onOpenTerms={() => setLegalPage('terms')} onOpenPrivacy={() => setLegalPage('privacy')} />
    </div>
  );
}

export default App;
